import socketIOClient from "socket.io-client";
import config from "./config";
import { parseJwt, b64toBlob } from "../utils/services";
import { getDimensions, setJobQtyData, setStockUnitData } from "../redux/actions";
import { setWebNotificationUnreadCount } from "../redux/actions/NotificationsSettings";
import { showToast } from "./functions";
import { lsGet, lsSet, lsRemove } from '../utils/hooks';

/* =========================
   ✅ CLEAN FILE NAME HELPER
========================= */
const getCleanFileName = (fileName, url) => {
    let name = fileName;

    // fallback from URL
    if (!name && url) {
        const parts = url.split('/');
        name = parts[parts.length - 1].split('?')[0];
    }

    if (!name) return "report";

    // remove UUID + timestamp
    name = name.replace(/_ID_[a-z0-9-]+-\d+/i, '');

    return name;
};

/* =========================
   SOCKET INIT
========================= */
function socketServices() {
    const token = lsGet("token");
    if (!token) {
        cleanupSocket();
        return null;
    }

    const tokenData = parseJwt(token);
    const user = lsGet("user");

    if (!user || !tokenData) {
        cleanupSocket();
        return null;
    }
    const { _id, name, email, base } = user;

    if (window.currentSocket && window.currentSocket.connected) {
        return window.currentSocket;
    }

    const socket = socketIOClient(config.socketURL, {
        transports: ['websocket', 'polling'],
        query: {
            name: name || "",
            userId: _id || "",
            userName: email || "",
            base: base || "",
            exp: tokenData.exp || "",
            iat: tokenData.iat
        }
    });

    window.currentSocket = socket;

    socket.on("connect", () => {
        console.log("✅ Socket Connected | ID:", socket.id);
    });

    socket.on("socket-added", (response) => {
        console.log("🟢 socket-added received:", response);
        if (response?.data) {
            lsSet("socketID", response.data);
        }
    });

    socket.on("disconnect", (reason) => {
        console.log("❌ Socket Disconnected:", reason);
    });

    socket.on("connect_error", (err) => {
        console.error("Socket connection error:", err.message);
    });

    return socket;
}

/* =========================
   SOCKET EVENTS HANDLER
========================= */
function Operation(obj_data, dispatch) {
    const { type, parcelID, data = "", success = false, message, fileName, isWithS3Buckket = false } = obj_data;

    if (type == 'ParcelDimensions' && parcelID) {
        dispatch(getDimensions({ parcelID }));
    }

    /* =========================
        REPORT DOWNLOAD FIX
    ========================= */
    else if (['LocationSuggestion', "ReportDownload"].includes(type)) {
        showToast(message, !success);
        if (!success) return;

        //  ONLY clean filename (no extension change)
        const finalFileName = getCleanFileName(fileName, data);

        if (isWithS3Buckket) {
            const anchor = document.createElement('a');
            anchor.href = data;
            anchor.download = finalFileName;
            anchor.target = '_blank';
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
        } else {
            let contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            let blob = b64toBlob(data, contentType);
            let url = URL.createObjectURL(blob);

            let link = document.createElement("a");
            link.href = url;
            link.download = finalFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => URL.revokeObjectURL(url), 1000);
        }

        console.log("socket-detected-event::", { type, finalFileName });
    }

    else if (type == "InventoryJourney") {
        dispatch(setStockUnitData(obj_data));
    }

    else if (type == "InventoryJourneyJobs") {
        dispatch(setJobQtyData(obj_data));
    }

    else if (type == "NotificationWeb") {
        dispatch(setWebNotificationUnreadCount(obj_data));
        showToast(data?.message, false);
    }

    else if (type == "AlertOnly") {
        showToast(message, !success);
    }

    /* =========================
       (UNCHANGED) PDF DOWNLOAD
    ========================= */
    else if (['DownloadPDF'].includes(type)) {
        showToast(message, !success);
        if (!success) return;

        const finalFileName = fileName && fileName.toLowerCase().endsWith('.pdf')
            ? fileName
            : `${fileName || 'report'}.pdf`;

        if (isWithS3Buckket) {
            const anchor = document.createElement('a');
            anchor.href = data;
            anchor.download = finalFileName;
            anchor.target = '_blank';
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
        } else {
            const blob = b64toBlob(data, 'application/pdf');
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = finalFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
    }
}

/* =========================
   CLEANUP
========================= */
function cleanupSocket(type=null,token) {
    if (window.currentSocket) {
        window.currentSocket.removeAllListeners();
        if(type == "logout"){
            window.currentSocket.emit('logout', {
                reason: 'user_initiated',
                token: token,
                timestamp: Date.now()
            });
        }
        setTimeout(() => window.currentSocket.disconnect(), 500);
        window.currentSocket = null;
    }
    lsRemove("socketID");
}

export { socketServices, Operation, cleanupSocket };